import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { PageHead, Lede, Card, Kpi, Note, Kv } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Table } from "../../components/Table";
import { QueryState } from "../../components/States";
import { useStatus } from "../../api/queries";
import { isRetryable } from "../../api/errors";
import { ts } from "../../lib/format";
import { ActionForm } from "../p2/Common";
import { brokenFirst, readReconDetail, readReconList, summarize, verdict, type ReconRow } from "./api";

const READ = { retry: (n: number, e: unknown) => n < 2 && isRetryable(e), retryDelay: 1_500 } as const;
const CHANNEL_NOTE =
  "通道 = kernel-self：纸上盘阶段通道回报由内核撮合明细重建，接真券商前此对账恒平，只能证明记账链路自洽，不能证明与券商一致。";

function ids(list: string[]): ReactNode {
  return list.length ? <span className="mono">{list.join("、")}</span> : "—";
}

function missing(value: string | number | null): ReactNode {
  return value === null ? <span className="faint">缺</span> : value;
}

export function ReconciliationScreen(): ReactNode {
  const list = useQuery<ReconRow[], Error>({ queryKey: ["g3", "reconciliations"], queryFn: readReconList, ...READ });
  const status = useStatus();
  const [selected, setSelected] = useState<string | null>(null);
  const detail = useQuery<ReconRow | null, Error>({
    queryKey: ["g3", "reconciliation", selected],
    queryFn: () => readReconDetail(selected as string),
    enabled: Boolean(selected),
    ...READ,
  });

  return (
    <>
      <PageHead
        title="对账"
        sub="GET /reconciliations · 通道 kernel-self"
        actions={
          status.status === "success" ? (
            status.data.halted ? (
              <Pill tone="halt">停机停手：{status.data.halt_reason ?? "原因未给"}</Pill>
            ) : (
              <Pill tone={status.data.balance.balanced ? "ok" : "bad"}>
                最近一次{status.data.balance.balanced ? "平" : "不平"} · 核对 {status.data.balance.checked} 笔
              </Pill>
            )
          ) : status.status === "error" ? (
            <Pill tone="warn">对账结论未取到</Pill>
          ) : null
        }
      />
      <Lede>
        本地账与通道回报逐笔核对：三类差异分列（通道有本地无 / 本地有通道无 / 数量价格不一致），平与不平只读后端的
        <code> balanced </code>与<code> summary</code>，前端不自行比对成交明细。现状每次运行收尾对一次。
      </Lede>
      <QueryState status={list.status} error={list.error} what="对账历史（GET /reconciliations）" refetch={() => void list.refetch()}>
        {list.status === "success" ? <History rows={list.data} selected={selected} onSelect={setSelected} /> : null}
      </QueryState>

      {selected ? (
        <div style={{ marginTop: 12 }}>
          <Card title={`单次明细 · ${selected}`} sub={`GET /reconciliations/${selected}`}>
            <QueryState status={detail.status} error={detail.error} what="对账明细" refetch={() => void detail.refetch()}>
              {detail.status === "success" ? (
                detail.data === null ? (
                  <Note tone="warn">
                    未对账：后端按 run_id 查不到 {selected} 的对账明细（404）。明细按 run_id 索引由缺陷单 #4748 修正；列表里的结论仍以上表为准。
                  </Note>
                ) : (
                  <Kv
                    pairs={[
                      ["结论", <Pill tone={verdict(detail.data).tone}>{verdict(detail.data).text}</Pill>],
                      ["已核笔数", detail.data.checked ?? "—"],
                      ["通道有本地无", ids(detail.data.missing_locally)],
                      ["本地有通道无", ids(detail.data.missing_on_channel)],
                      ["数量价格不一致", ids(detail.data.mismatched)],
                      ["摘要", detail.data.summary ?? "—"],
                    ]}
                  />
                )
              ) : null}
            </QueryState>
          </Card>
        </div>
      ) : null}

      <div style={{ marginTop: 12 }}>
        <ActionForm
          path="/reconciliations/run"
          title="再对一次（对当前回测账以 kernel-self 通道再核一次；不平会触发停机）"
          fields={[
            { name: "operator", label: "operator" },
            { name: "reason", label: "reason" },
          ]}
        />
        <Note>再对一次不会解除停机，恢复在停机屏（#halt）。{CHANNEL_NOTE}</Note>
      </div>
    </>
  );
}

function History({ rows, selected, onSelect }: { rows: ReconRow[]; selected: string | null; onSelect: (id: string) => void }): ReactNode {
  if (rows.length === 0) {
    return (
      <Card title="对账历史">
        <div className="empty">
          还没有对账记录：纸上盘还没跑过（对账在每次「跑一天」收尾时做）。<a href="#/replay">去运行与回放 →</a>
        </div>
      </Card>
    );
  }
  const s = summarize(rows);
  const anyDiff = rows.some((r) => r.missing_locally.length || r.missing_on_channel.length || r.mismatched.length);
  return (
    <>
      <div className="grid g4" style={{ marginBottom: 12 }}>
        <Kpi label="运行次数" value={s.total} />
        <Kpi label="平" value={<span className="pos">{s.balanced}</span>} />
        <Kpi label="不平" value={<span className={s.broken ? "neg" : "pos"}>{s.broken}</span>} detail={s.broken ? "对账不平即停机，不自动恢复" : undefined} />
        <Kpi label="未对账" value={s.unreconciled} />
      </div>
      <Card
        title="对账历史"
        sub={s.allBalanced ? `全部 ${s.total} 次运行逐笔平 · 通道 kernel-self` : "不平的行置顶；点一行看单次明细"}
        nopad
      >
        <Table
          cols={[
            { label: "运行", mono: true },
            { label: "策略" },
            { label: "交易日" },
            { label: "时间" },
            { label: "通道" },
            { label: "已核", num: true },
            { label: "结论" },
            ...(anyDiff
              ? [{ label: "通道有本地无", wrap: true }, { label: "本地有通道无", wrap: true }, { label: "数量价格不一致", wrap: true }]
              : []),
          ]}
          rows={brokenFirst(rows).map((r) => {
            const v = verdict(r);
            return {
              key: r.run_id,
              cls: r.balanced === false ? "bad" : "",
              selected: r.run_id === selected,
              onClick: () => onSelect(r.run_id),
              cells: [
                r.run_id,
                missing(r.strategy_id),
                missing(r.trade_date),
                r.ts_ns === null ? <span className="faint">缺</span> : ts(r.ts_ns),
                missing(r.channel),
                r.checked ?? "—",
                <>
                  <Pill tone={v.tone}>{v.text}</Pill>
                  {r.balanced === false ? (
                    <>
                      {" "}
                      <a href="#/halt">导致停机 · 去停机屏 →</a>
                    </>
                  ) : null}
                </>,
                ...(anyDiff ? [ids(r.missing_locally), ids(r.missing_on_channel), ids(r.mismatched)] : []),
              ],
            };
          })}
        />
      </Card>
      <Note>
        {rows.some((r) => r.strategy_id === null || r.trade_date === null || r.ts_ns === null || r.channel === null)
          ? "「策略 / 交易日 / 时间 / 通道」后端没给的格显示「缺」（旧运行记录或接库模式不带运行时间）；"
          : ""}
        比对键为 trade_id、client_order_id、instrument_id，再比数量与价格。
      </Note>
    </>
  );
}
